import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import NotificationInbox from "./NotificationInbox";

const LOGO_URL = "/logo.png";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);

  // Poll teacher's pending booking count every 30s while signed in as teacher
  useEffect(() => {
    if (!user || user.role !== "teacher") {
      setPendingCount(0);
      return;
    }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/bookings/me");
        if (cancelled) return;
        const count = (data || []).filter((b) => b.status === "pending_teacher_approval").length;
        setPendingCount(count);
      } catch {
        /* silent */
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, location.pathname]);

  const goAccount = () => {
    if (!user) return navigate("/login");
    if (user.role === "teacher") navigate("/teacher/dashboard");
    else if (user.role === "parent") navigate("/parent/dashboard");
    else if (user.role === "admin") navigate("/admin/dashboard");
    else navigate("/home");
  };

  // Hide navbar ONLY on the landing splash `/` — the splash shows just the
  // centered animated logo. Every other page keeps the top navbar with logo,
  // Home/Login/Signup/Logout controls.
  if (location.pathname === "/") return null;

  return (
    <header
      className="w-full bg-[#FFF7ED] border-b border-black/5 sticky top-0 z-40 backdrop-blur-sm"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 sm:py-3">
        {/* Top row: Home (left) · Logo (center) · Login/Logout (right) */}
        <div className="grid grid-cols-3 items-center gap-2">
          <div className="flex items-center justify-start gap-2">
            {!user ? (
              <Link
                to="/signup"
                className="btn-yellow px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-base"
                data-testid="navbar-signup-link"
              >
                Sign Up
              </Link>
            ) : (
              <Link
                to="/home"
                className="btn-outline-teal px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-base"
                data-testid="navbar-home-link"
              >
                Home
              </Link>
            )}
          </div>

          <div className="flex items-center justify-center" data-testid="navbar-center">
            <Link to="/home" className="flex items-center" data-testid="navbar-logo-link">
              <img
                src={LOGO_URL}
                alt="Rate-ED"
                className="h-24 sm:h-36 md:h-40 w-auto object-contain"
                data-testid="navbar-logo-img"
              />
            </Link>
          </div>

          <div className="flex items-center justify-end gap-2">
            {!user ? (
              <Link
                to="/login"
                className="btn-outline-teal px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-base"
                data-testid="navbar-login-link"
              >
                Login
              </Link>
            ) : (
              <button
                onClick={logout}
                className="btn-outline-teal px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-base"
                data-testid="navbar-logout-button"
              >
                Logout
              </button>
            )}
          </div>
        </div>

        {/* Bottom row: Dashboard tab on its own line, centered below the logo (only when logged in) */}
        {user && (
          <div
            className="flex items-center justify-center gap-3 mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-black/5"
            data-testid="navbar-dashboard-row"
          >
            <button
              onClick={goAccount}
              className="btn-yellow px-6 sm:px-8 py-2 text-sm sm:text-base relative w-full sm:w-auto max-w-xs"
              data-testid="navbar-dashboard-button"
            >
              Dashboard
              {pendingCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1 ring-2 ring-[#FFF7ED]"
                  style={{ background: "#DC2626", color: "#FFFFFF" }}
                  title={`${pendingCount} pending booking request${pendingCount > 1 ? "s" : ""}`}
                  data-testid="navbar-pending-badge"
                >
                  {pendingCount > 9 ? "9+" : pendingCount}
                </span>
              )}
            </button>
            <NotificationInbox />
          </div>
        )}
      </div>
    </header>
  );
}
